import { FC, Fragment, useEffect, useState, useRef, useContext } from 'react';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { RoomType, roomTypesIcon, RoomTypeGrp, RoomTypeOption } from 'ts/hotel/classes/FilterModel';
import FilterRoomType from '../Result/Filters/FilterRoomType';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import { topBed, topCategories, topType } from 'constants/hotel';
import FormGroup from 'components/ui/FormGroup/FormGroup';
import { useTranslation } from 'react-i18next';
import { HotelDetailsContext } from 'contexts/HotelDetailsContext';
import { Hotel, HotelRoom, Price } from 'ts/hotel/interfaces/HotelDetails';
import { createPortal } from 'react-dom';

interface RoomDetailsAiProps {
  hotel?: Hotel;
}

const RoomDetailsAi: FC<RoomDetailsAiProps> = ({ hotel }) => {
  const { t } = useTranslation();
  const currency = sessionStorage.getItem('displayCurrencyHotels') || 'usd';
  const { roomTypes, setRoomTypes } = useContext(HotelDetailsContext);
  const prevRoomTypesRef = useRef<RoomTypeGrp[]>([]);
  const [freeTextSearch, setFreeTextSearch] = useState('');
  const [displayFilter, setDisplayFilter] = useState<RoomTypeGrp[]>([]);

  useEffect(() => {
    if (!hotel) return;
    const newRoomTypes: RoomTypeGrp[] = Object.keys(RoomType).map((key) => ({
      title: key,
      options: [] as RoomTypeOption[],
      icon: roomTypesIcon[key as keyof typeof roomTypesIcon],
    }));

    newRoomTypes.forEach((type) => {
      const optionsMap = new Map<string, RoomTypeOption>();

      hotel.roomClasses.forEach((rClass) => {
        const valData = rClass.hotelRooms[0][type.title as keyof HotelRoom];
        if (!valData) return;

        valData.split(', ').forEach((val: any) => {
          if (!val) return;

          const existingOption = optionsMap.get(val);

          if (existingOption) {
            existingOption.count++;
            existingOption.price = Math.min(
              existingOption.price,
              rClass.price[currency as keyof Price]
            );
          } else {
            // Preserve checked state from previous roomTypes if it exists
            const prevType = prevRoomTypesRef.current.find((rt) => rt.title === type.title);
            const prevOption = prevType?.options.find((opt) => opt.name === val);
            const wasChecked = prevOption?.checked || false;

            optionsMap.set(val, {
              name: val,
              checked: wasChecked,
              count: 1,
              price: rClass.price[currency as keyof Price],
              visible: true,
            });
          }
        });
      });

      type.options = Array.from(optionsMap.values()).sort((a, b) => a.name.localeCompare(b.name));
    });

    prevRoomTypesRef.current = newRoomTypes;
    setRoomTypes(newRoomTypes);
    setDisplayFilter(newRoomTypes);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotel?.roomClasses?.length]);

  useEffect(() => {
    setDisplayFilter(roomTypes);
  }, [roomTypes]);

  const onChangeRoom = (group: RoomTypeGrp) => {
    const newRoomTypes = roomTypes.map((it) => {
      if (it.title === group.title) {
        return group;
      }
      return it;
    });
    setRoomTypes(newRoomTypes);
  };

  const onClickClearAll = () => {
    const newRoomTypes = roomTypes.map((it) => {
      if (it.title === RoomType.ada) {
        return it;
      } else {
        return { ...it, options: it.options.map((opt) => ({ ...opt, checked: false })) };
      }
    });
    setRoomTypes(newRoomTypes);
  };

  const isClearVisible = (): boolean => {
    return roomTypes
      .filter((it) => it.title !== RoomType.ada)
      .some((it) => it.options.some((opt) => opt.checked));
  };

  const onChangeFreeTextSearch = (text: string) => {
    setFreeTextSearch(text);

    const rt = roomTypes.map((it) => {
      return {
        ...it,
        options: it.options.map((opt) => {
          return {
            ...opt,
            visible: opt.name?.toLowerCase().includes(text.toLowerCase()),
          };
        }),
      };
    });

    setDisplayFilter(rt);
  };

  const getType = (title: string): RoomTypeGrp | undefined => {
    const rt = displayFilter.find((it) => it.title === title);
    const options = rt?.options.filter((it) => it.visible !== false);

    return options && rt ? { ...rt, options } : undefined;
  };

  if (!document.querySelector('#rooms-details')) return null;

  return createPortal(
    <div className="default-shadow details-summary mt-20" id="rooms-details-ai">
      <div className="flex-between room-details-ai-header">
        <h5 className="title title-header-grey">
          {!!roomTypes.length ? (
            <span>{t('hotel.filter_by_room_type')}</span>
          ) : (
            <div style={{ height: 20 }}></div>
          )}
        </h5>
        {isClearVisible() && (
          <span
            className="text-normal text-light ml-10 color-danger cur-poi"
            onClick={onClickClearAll}
          >
            <span className="mr-5">{t('clear_all')}</span>
            <FontAwesomeIcon className="regular-svg" icon={faTimes} />
          </span>
        )}
      </div>

      {!!roomTypes.length && (
        <FormGroup
          label=""
          className="mb-10 display-ib movable-placeholder-xsm"
          movablePlaceholder={t('search.search') + '...'}
          onChange={onChangeFreeTextSearch}
          value={freeTextSearch}
          movablePlaceholderSm={true}
          movablePlaceholderIcon={faSearch}
        />
      )}
      <Fragment>
        {!!roomTypes.length && (
          <div className="flex-row pr-10 flex-between">
            <div className="w100p">
              {!!getType(RoomType.ada)?.options.length && (
                <FilterRoomType
                  type={getType(RoomType.ada)}
                  onChange={onChangeRoom}
                  top={topCategories}
                />
              )}
              {!!getType(RoomType.typ)?.options.length && (
                <FilterRoomType
                  type={getType(RoomType.typ)}
                  onChange={onChangeRoom}
                  top={topType}
                />
              )}
              {!!getType(RoomType.blc)?.options.length && (
                <FilterRoomType type={getType(RoomType.blc)} onChange={onChangeRoom} />
              )}
              {!!getType(RoomType.cap)?.options.length && (
                <FilterRoomType type={getType(RoomType.cap)} onChange={onChangeRoom} />
              )}
              {!!getType(RoomType.sgl)?.options.length && (
                <FilterRoomType type={getType(RoomType.sgl)} onChange={onChangeRoom} />
              )}
              {!!getType(RoomType.pax)?.options.length && (
                <FilterRoomType type={getType(RoomType.pax)} onChange={onChangeRoom} />
              )}
              {!!getType(RoomType.cat)?.options.length && (
                <FilterRoomType type={getType(RoomType.cat)} onChange={onChangeRoom} />
              )}
              {!!getType(RoomType.vew)?.options.length && (
                <FilterRoomType type={getType(RoomType.vew)} onChange={onChangeRoom} />
              )}
              {!!getType(RoomType.bed)?.options.length && (
                <FilterRoomType type={getType(RoomType.bed)} onChange={onChangeRoom} top={topBed} />
              )}
              {!!getType(RoomType.bdr)?.options.length && (
                <FilterRoomType type={getType(RoomType.bdr)} onChange={onChangeRoom} />
              )}
              {!!getType(RoomType.brd)?.options.length && (
                <FilterRoomType type={getType(RoomType.brd)} onChange={onChangeRoom} />
              )}
            </div>
          </div>
        )}
      </Fragment>
    </div>,
    document.querySelector('#rooms-details') as Element
  );
};

export default withErrorBoundary(RoomDetailsAi, 'RoomDetailsAi');
